/**
 * Schedule non-critical third-party work (analytics, monitoring, ad pixels)
 * fully off the initial-load critical path.
 *
 * Runs the callback on the first real user interaction, or shortly after the
 * window `load` event settles — whichever comes first. `requestIdleCallback`
 * is not enough here: it fires in the first main-thread gap mid-hydration,
 * which still lands third-party fetches inside the initial-load window.
 * Visitors who leave before interacting and before the post-load delay never
 * run the callback at all — acceptable for tracking/monitoring, so don't use
 * this for work the app depends on. SSR-safe — no-ops outside a browser.
 */

export const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const;

const DEFAULT_POST_LOAD_DELAY_MS = 3000;

export const deferNonCritical = (
  fn: () => void,
  {
    postLoadDelayMs = DEFAULT_POST_LOAD_DELAY_MS,
  }: { postLoadDelayMs?: number } = {}
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  let fired = false;
  const cleanups: Array<() => void> = [];

  const fire = () => {
    if (fired) {
      return;
    }
    fired = true;
    for (const cleanup of cleanups) {
      cleanup();
    }
    fn();
  };

  for (const eventName of INTERACTION_EVENTS) {
    const onInteraction = () => fire();
    // Capture phase so scrolls inside nested containers count as engagement
    // (scroll events don't bubble); passive so we never delay the gesture.
    window.addEventListener(eventName, onInteraction, {
      capture: true,
      once: true,
      passive: true,
    });
    cleanups.push(() =>
      window.removeEventListener(eventName, onInteraction, { capture: true })
    );
  }

  const startPostLoadTimer = () => {
    if (fired) {
      return;
    }
    const timerId = setTimeout(fire, postLoadDelayMs);
    cleanups.push(() => clearTimeout(timerId));
  };

  if (document.readyState === 'complete') {
    startPostLoadTimer();
  } else {
    const onLoad = () => startPostLoadTimer();
    window.addEventListener('load', onLoad, { once: true });
    cleanups.push(() => window.removeEventListener('load', onLoad));
  }
};
